import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useFinishes } from '@/api/finishes';
import { DEFAULT_PANEL_FINISH, componentDims, resolveFoamLayers, resolveOversize } from '@estimator/shared';
import PanelDimControls from './shared/PanelDimControls';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Divider, Select, SelectItem, Switch, Accordion, AccordionItem,
  Autocomplete, AutocompleteItem,
  useDisclosure,
} from '@heroui/react';
import { Armchair, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateLineItem, useUpdateLineItem } from '@/api/line-items';
import { useProject } from '@/api/projects';
import { useMaterialsLibrary } from '@/api/materials-library';
import { useHardwareLibrary } from '@/api/hardware-library';
import { useBackCushionTemplates } from '@/api/banquette-back-cushion-templates';
import { useDrawingViewport } from '@/api/drawing-viewports';
import { usePageTileInfo } from '@/api/knowledge';
import BackCushionDesignSelector from './back-cushion/BackCushionDesignSelector';
import CushionSpringConfig from './shared/CushionSpringConfig';
import BackCushionFramingConfig from './back-cushion/BackCushionFramingConfig';
import BanquetteViewportSelector from './banquette/BanquetteViewportSelector';
import BackCushionCostPreview from './back-cushion/BackCushionCostPreview';
import BackCushionSlotManager from './back-cushion/BackCushionSlotManager';
import BackCushionFinishSummary from './back-cushion/BackCushionFinishSummary';
import FinishSchedule from './FinishSchedule';
import ComponentNestingPanel from './ComponentNestingPanel';
import { calcBackCushionCostPreview, resolveChannelCount, resolveButtonCount, ProjectCostContext } from '@/utils/back-cushion-cost';
import { useComponentCncCost } from '@/hooks/useComponentCncCost';
import { useEstimateBanquetteLabor } from '@/api/labor-rates';
import { useUpholsteryDesignSettings } from '@/api/upholstery-design-settings';
import { useUpholsteryDesigns } from '@/api/upholstery-designs';
import { useOversizeRulesState } from '@/api/oversize-settings';
import { resolveDesignTypeAllowancePct, resolveDesignTraits, ALL_FACES, type FaceSelection } from '@estimator/shared';
import FabricCoverageSection from './FabricCoverageSection';
import { OversizeControl } from './OversizeControl';
import { evalMathInput, inchesToFeetDisplay } from '@/utils/math-input';
import { toTemplatePayload } from '@/utils/line-item-template';
import { useBackCushionSlotStore } from '@/store/backCushionSlotStore';
import { priceMaterialByUnit } from '@estimator/shared';
import type {
  BackCushionConfig,
  BackCushionDesign,
  BackCushionSpringConfig as BackCushionSpringConfigType,
  BackCushionFraming,
  BackCushionFoamLayer,
  MaterialLibrary,
} from '@estimator/shared';

interface BackCushionModalProps {
  isOpen: boolean;
  onClose: () => void;
  areaId: string;
  projectId: string;
  existingLineItemId?: string | null;
  existingConfig?: string | null;
  existingDimensions?: { width: number; height: number; depth: number } | null;
  templateMode?: boolean;
}

const DEFAULT_DESIGN: BackCushionDesign = {
  type: 'plain',
  channel_width: null,
  channel_count_override: null,
  button_tuftings_per_foot: null,
  button_hardware_id: null,
  wire_hardware_id: null,
};

const DEFAULT_SPRINGS: BackCushionSpringConfigType = {
  springs_enabled: false,
  spring_hardware_id: null,
  spring_mode: 'auto',
  spring_spacing: 4,
  spring_count: 0,
  clip_hardware_id: null,
  connecting_wire_hardware_id: null,
  connecting_wire_count: 1,
};

// First-enable defaults (auto-filled the first time the user toggles springs ON,
// only into fields that are still empty/null/at-schema-default).
const SPRING_DEFAULT_HARDWARE_NAMES = {
  spring: 'No Sag #11 Bundle 120 ft',
  clip: 'Spring Clip with Plastic - EK',
  connecting_wire: 'Stay Wire',
} as const;
const SPRING_DEFAULT_SPACING = 6;

/** Inclusive heuristic mirroring migration 033 — used when loading older configs
 *  that pre-date springs_enabled, so the toggle defaults match DB-backfilled state. */
function deriveSpringsEnabledFromLegacy(s: any): boolean {
  if (!s) return false;
  if (s.spring_hardware_id) return true;
  if (s.clip_hardware_id) return true;
  if (s.connecting_wire_hardware_id) return true;
  if (s.spring_mode === 'manual' && (s.spring_count ?? 0) > 0) return true;
  return false;
}

const DEFAULT_FRAMING: BackCushionFraming = {
  side_panel_material_id: null,
  rib_material_id: null,
  default_finish_id: null,
  side_panel_finish: DEFAULT_PANEL_FINISH,
  rib_finish: DEFAULT_PANEL_FINISH,
  rib_mode: 'auto',
  rib_spacing: 24,
  rib_count: 0,
  waste_factor: 0.10,
  curved_section_count: 0,
  curved_section_waste_factor: 0,
  rails: [
    { name: 'Top Panel', width: 3, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Bottom Panel', width: 4, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Foam Contour Panel', width: 10, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Back Top Rail', width: 3, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
    { name: 'Front Bottom Rail', width: 3, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH },
  ],
  additional_panels: [],
};

export default function BackCushionModal({
  isOpen, onClose, areaId, projectId,
  existingLineItemId, existingConfig, existingDimensions,
  templateMode = false,
}: BackCushionModalProps) {
  // ── State ──
  const [code, setCode] = useState('BC');
  const [name, setName] = useState('');
  const [widthInches, setWidthInches] = useState(120);
  const [widthInputStr, setWidthInputStr] = useState('120');
  const [profileHeight, setProfileHeight] = useState(18);
  const [profileDepth, setProfileDepth] = useState(6);
  const [baseDepth, setBaseDepth] = useState<number | null>(null);
  const [baseHeight, setBaseHeight] = useState<number | null>(null);
  const [baseWidth, setBaseWidth] = useState<number | null>(null);
  const [manualSections, setManualSections] = useState(false);
  const [manualSectionCount, setManualSectionCount] = useState(1);
  const [design, setDesign] = useState<BackCushionDesign>(DEFAULT_DESIGN);
  // Substrate mode is always 'full_frame' (no backer-only mode)
  const [framing, setFraming] = useState<BackCushionFraming>(DEFAULT_FRAMING);
  const [springs, setSprings] = useState<BackCushionSpringConfigType>(DEFAULT_SPRINGS);
  const [foamLayers, setFoamLayers] = useState<BackCushionFoamLayer[]>([]);
  const [foamWasteFactor, setFoamWasteFactor] = useState(0.10);
  const [foamCurvedWasteFactor, setFoamCurvedWasteFactor] = useState(0);
  const [dacronMaterialId, setDacronMaterialId] = useState<string | null>(null);
  const [dacronWasteFactor, setDacronWasteFactor] = useState(0.10);
  const [dacronCurvedWasteFactor, setDacronCurvedWasteFactor] = useState(0);
  const [viewportId, setViewportId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [fabricDesignPct, setFabricDesignPct] = useState<number | null>(null);
  const [fabricPatternAllowancePct, setFabricPatternAllowancePct] = useState(0);
  const [fabricFaces, setFabricFaces] = useState<FaceSelection>({ ...ALL_FACES });
  // Library pricing only. Absent on a saved config means on.
  const [oversizeEnabled, setOversizeEnabled] = useState(true);

  // Seeding rule: the first time any panel on this component is linked, pin the
  // base to the current live dimensions. The `prev == null` guard is what makes
  // this a one-time seed rather than an auto-follow -- an auto-follow would hold
  // every delta at zero forever and the feature would silently do nothing.
  useEffect(() => {
    const linked =
      (framing.rails ?? []).some((r) => r.width_link) ||
      (framing.additional_panels ?? []).some((p) => p.width_link || p.height_link) ||
      // A foam layer may track any axis, so any foam link seeds all three bases.
      foamLayers.some((f) => f.width_link || f.length_link);
    if (!linked) return;
    setBaseDepth((prev) => (prev == null ? profileDepth : prev));
    setBaseHeight((prev) => (prev == null ? profileHeight : prev));
    setBaseWidth((prev) => (prev == null ? widthInches : prev));
  }, [framing, foamLayers, profileDepth, profileHeight, widthInches]);

  // Resolved foam sizes for the row notes. The numeric inputs keep showing the
  // BASE, so the number you typed is always the number you can edit.
  const resolvedFoam = useMemo(
    () => resolveFoamLayers(foamLayers, componentDims({
      lf_quantity: widthInches / 12,
      profile_depth: profileDepth,
      profile_height: profileHeight,
      base_width: baseWidth,
      base_depth: baseDepth,
      base_height: baseHeight,
    })),
    [foamLayers, widthInches, profileDepth, profileHeight, baseWidth, baseDepth, baseHeight],
  );

  const slots = useBackCushionSlotStore((s) => s.slots);
  const setSlots = useBackCushionSlotStore((s) => s.setSlots);
  const updateSlot = useBackCushionSlotStore((s) => s.updateSlot);
  const slotManagerModal = useDisclosure();

  // ── API ──
  const createLineItem = useCreateLineItem();
  const updateLineItem = useUpdateLineItem();
  const { data: project } = useProject(projectId);
  const { data: finishes = [] } = useFinishes();
  const { data: materials = [] } = useMaterialsLibrary();
  const { data: hardwareList = [] } = useHardwareLibrary();
  const { data: templates = [] } = useBackCushionTemplates();
  const { data: upholsterySettings } = useUpholsteryDesignSettings();
  // Cached under one key with staleTime: Infinity — UpholsteryDesignSelector
  // below already calls this independently, so this second call costs no
  // extra request. Kept separate rather than prop-drilled through the
  // selector so the cost preview and the selector stay decoupled.
  const { data: designCatalog } = useUpholsteryDesigns();
  const { state: oversizeRules } = useOversizeRulesState();

  // ── Project context for currency conversion + nesting cost mode ──
  const projectCtx = useMemo<ProjectCostContext | undefined>(() => {
    if (!project) return undefined;
    let nestingOverrides: Record<string, string> = {};
    try {
      const raw = (project as any).nesting_cost_overrides;
      if (raw) nestingOverrides = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch { /* ignore */ }
    return {
      mxn_exchange_rate: (project as any).mxn_exchange_rate ?? null,
      mxn_bank_spread: (project as any).mxn_bank_spread ?? 0,
      nesting_cost_overrides: nestingOverrides,
    };
  }, [project]);

  const isEditMode = !!existingLineItemId;

  // ── Derived ──
  const lfQuantity = widthInches / 12;
  const sectionCount = manualSections
    ? manualSectionCount
    : Math.max(1, Math.ceil(widthInches / 96));
  const sectionLength = sectionCount > 0 ? widthInches / sectionCount : 0;

  // Build maps for cost preview
  const materialsMap = useMemo(() => {
    const map = new Map<string, MaterialLibrary>();
    for (const m of materials) map.set(m.id, m as MaterialLibrary);
    return map;
  }, [materials]);

  const hardwareMap = useMemo(() => {
    const map = new Map<string, { cost_per_unit: number; name: string; unit?: string; currency?: string; iva_percent?: number }>();
    for (const h of hardwareList as any[]) {
      map.set(h.id, {
        cost_per_unit: h.cost_per_unit ?? 0,
        name: h.name,
        unit: h.unit,
        currency: h.currency,
        iva_percent: h.iva_percent,
      });
    }
    return map;
  }, [hardwareList]);

  const hardwareOptions = useMemo(() => {
    return (hardwareList as any[]).map((h) => ({ id: h.id, name: h.name }));
  }, [hardwareList]);

  // Fetch the viewport's page PPI for cost preview calculations
  const { data: viewportData } = useDrawingViewport(viewportId ?? '');
  const { data: tileInfo } = usePageTileInfo(viewportData?.page_id ?? null);
  const pageScalePpi = tileInfo?.scalePixelsPerUnit ?? undefined;

  // The component library (Cria) renders these modals with no project at all.
  // In that context there is no project Nesting Cost Mode to honour, so sheet
  // goods are priced proportionally — matching the server's ex-fabric path.
  const isLibraryContext = templateMode || !projectId;

  // ── Cost Preview ──
  // The config the cost preview prices. Oversize resolves against this same
  // object, so its factor follows the dimensions as they are typed.
  const previewConfig = useMemo((): Partial<BackCushionConfig> => ({
    product_type: 'banquette_back_cushion',
    lf_quantity: lfQuantity,
    section_count: sectionCount,
    profile_height: profileHeight,
    profile_depth: profileDepth,
    base_depth: baseDepth,
    base_width: baseWidth,
    base_height: baseHeight,
    design,
    substrate_mode: 'full_frame' as const,
    backer_panel: null,
    framing,
    springs,
    foam_layers: foamLayers,
    foam_waste_factor: foamWasteFactor,
    foam_curved_waste_factor: foamCurvedWasteFactor,
    dacron_material_id: dacronMaterialId,
    dacron_waste_factor: dacronWasteFactor,
    dacron_curved_waste_factor: dacronCurvedWasteFactor,
    fabric_design_pct: fabricDesignPct,
    fabric_pattern_allowance_pct: fabricPatternAllowancePct,
    fabric_faces: fabricFaces,
    slots,
  }), [
    widthInches, sectionCount, profileHeight, profileDepth, baseDepth, baseHeight, baseWidth,
    design, framing, springs,
    foamLayers, foamWasteFactor, foamCurvedWasteFactor, dacronMaterialId, dacronWasteFactor, dacronCurvedWasteFactor,
    fabricDesignPct, fabricPatternAllowancePct, fabricFaces,
    slots,
  ]);

  const costPreview = useMemo(() => calcBackCushionCostPreview(
    previewConfig,
    materialsMap,
    hardwareMap,
    pageScalePpi,
    projectCtx,
    upholsterySettings?.design_allowance_by_type,
    // The component library has no project, so there is no Nesting Cost Mode
    // to honour. Force proportional pricing to match ComponentExFabricService,
    // which already forces used_area for the Cria Layout integration.
    // Keyed off the missing projectId, not templateMode — the library passes
    // templateMode={!editingTemplate}, so it is false while EDITING a template.
    {
      ...(isLibraryContext ? { forceCostMode: 'used_area' as const } : {}),
      finishes,
      designCatalog,
    },
    upholsterySettings?.default_button_hardware_id ?? null,
    upholsterySettings?.default_wire_hardware_id ?? null,
  ), [
    previewConfig, pageScalePpi, materialsMap, hardwareMap, projectCtx,
    upholsterySettings,
    isLibraryContext,
    finishes, designCatalog,
  ]);

  // The factor the Library applies to this template's ex-fabric total, at the
  // dimensions currently typed.
  const oversize = useMemo(() => resolveOversize(
    oversizeRules, 'banquette_back_cushion', { ...previewConfig, oversize_enabled: oversizeEnabled },
  ), [oversizeRules, previewConfig, oversizeEnabled]);

  // ── CNC cost, amortized over the company reference batch ──
  const cnc = useComponentCncCost(
    costPreview?.nesting_parts ?? [],
    materials as MaterialLibrary[],
  );

  // Resolved per-design-type allowance for the modal placeholder.
  // Null means "no setting configured" — placeholder falls back to material default.
  const resolvedDesignPct = useMemo<number | null>(() => {
    if (!upholsterySettings) return null;
    const traits = resolveDesignTraits(design.type, designCatalog);
    const channelsPerSection = resolveChannelCount(design, sectionLength, profileHeight, traits);
    const totalButtons = resolveButtonCount(design, lfQuantity, traits);
    const buttonsPerSection = totalButtons / Math.max(1, sectionCount);
    return resolveDesignTypeAllowancePct(
      design.type,
      upholsterySettings.design_allowance_by_type,
      channelsPerSection,
      buttonsPerSection,
      traits,
    );
  }, [upholsterySettings, design, sectionLength, profileHeight, lfQuantity, sectionCount, designCatalog]);

  // ── Labor Estimate (debounced API call) ──
  const laborEstimate = useEstimateBanquetteLabor();
  const laborTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (laborTimerRef.current) clearTimeout(laborTimerRef.current);
    laborTimerRef.current = setTimeout(() => {
      const config = {
        product_type: 'banquette_back_cushion',
        lf_quantity: lfQuantity,
        section_count: sectionCount,
        profile_height: profileHeight,
        profile_depth: profileDepth,
        base_depth: baseDepth,
        base_width: baseWidth,
        base_height: baseHeight,
        design,
        substrate_mode: 'full_frame',
        framing,
        springs,
        foam_layers: foamLayers,
        // Fabric-yards complexity weight needs the page PPI to convert
        // polyline pixels → real inches, plus the same design/pattern
        // multipliers the cost calc uses. Without these, fabric_yards = 0
        // and the "Fabric Cutting" labor row drops out of the breakdown.
        image_scale_ppi: pageScalePpi ?? 0,
        fabric_design_pct: fabricDesignPct,
        fabric_pattern_allowance_pct: fabricPatternAllowancePct,
      fabric_faces: fabricFaces,
        slots,
      };
      laborEstimate.mutate(config);
    }, 500);
    return () => { if (laborTimerRef.current) clearTimeout(laborTimerRef.current); };
  }, [isOpen, widthInches, sectionCount, profileHeight, profileDepth, baseDepth, baseHeight, baseWidth, design, framing, springs, foamLayers, slots, pageScalePpi, fabricDesignPct, fabricPatternAllowancePct, fabricFaces]);

  // ── Load existing config ──
  useEffect(() => {
    if (!isOpen) return;
    if (existingConfig) {
      try {
        const bc = JSON.parse(existingConfig);
        setCode(bc.code ?? 'BC');
        setName(bc.name ?? '');
        setManualSections(bc.manual_sections ?? false);
        setManualSectionCount(bc.section_count ?? 1);
        setDesign(bc.design ?? DEFAULT_DESIGN);
        setFraming(bc.framing ?? DEFAULT_FRAMING);
        // Older configs pre-date `springs_enabled` — derive it from any
        // populated spring fields so the toggle reflects the real state
        // even if migration 033 hasn't been applied for this row yet.
        const loadedSprings = bc.springs
          ? {
              ...DEFAULT_SPRINGS,
              ...bc.springs,
              springs_enabled:
                typeof bc.springs.springs_enabled === 'boolean'
                  ? bc.springs.springs_enabled
                  : deriveSpringsEnabledFromLegacy(bc.springs),
            }
          : DEFAULT_SPRINGS;
        setSprings(loadedSprings);
        setFoamLayers(bc.foam_layers ?? []);
        setFoamWasteFactor(bc.foam_waste_factor ?? 0.10);
        setFoamCurvedWasteFactor(bc.foam_curved_waste_factor ?? 0);
        setDacronMaterialId(bc.dacron_material_id ?? null);
        setDacronWasteFactor(bc.dacron_waste_factor ?? 0.10);
        setDacronCurvedWasteFactor(bc.dacron_curved_waste_factor ?? 0);
        setViewportId(bc.viewport_id ?? null);
        setFabricDesignPct(bc.fabric_design_pct ?? null);
        setFabricPatternAllowancePct(bc.fabric_pattern_allowance_pct ?? 0);
        setFabricFaces({ ...ALL_FACES, ...(bc.fabric_faces ?? {}) });
        setOversizeEnabled(bc.oversize_enabled !== false);
        setSlots(bc.slots ?? []);

        if (existingDimensions && existingDimensions.width > 0) {
          const w = existingDimensions.width;
          setWidthInches(w);
          setWidthInputStr(w.toString());
          setProfileHeight(existingDimensions.height > 0 ? existingDimensions.height : (bc.profile_height ?? 18));
          setProfileDepth(existingDimensions.depth > 0 ? existingDimensions.depth : (bc.profile_depth ?? 6));
        } else {
          const w = Math.round((bc.lf_quantity ?? 10) * 12);
          setWidthInches(w);
          setWidthInputStr(w.toString());
          setProfileDepth(bc.profile_depth ?? 6);
          setProfileHeight(bc.profile_height ?? 18);
        }
        // Base dims come from the config only, never from existingDimensions.
        // Overwriting them with the line item's live size would zero every
        // delta and silently disable dynamic panels.
        setBaseDepth(bc.base_depth ?? null);
        setBaseWidth(bc.base_width ?? null);
        setBaseHeight(bc.base_height ?? null);
      } catch {
        // Invalid config, use defaults
      }
    }
  }, [isOpen, existingConfig, existingDimensions, setSlots]);

  // ── Springs Toggle ──
  // First-time-on auto-fill: when user flips springs_enabled false → true,
  // populate the four reference defaults by exact-name lookup, but only into
  // fields that are still empty. Subsequent toggles never overwrite.
  const handleSpringsToggle = useCallback((nextEnabled: boolean) => {
    if (!nextEnabled) {
      setSprings((s) => ({ ...s, springs_enabled: false }));
      return;
    }

    setSprings((s) => {
      const findHwIdByName = (name: string): string | null => {
        const target = name.trim();
        const match = (hardwareList as any[]).find(
          (h) => typeof h?.name === 'string' && h.name.trim() === target,
        );
        if (!match) {
          // eslint-disable-next-line no-console
          console.warn(`[springs] hardware not found by name: "${name}"`);
          return null;
        }
        return match.id as string;
      };

      const next: BackCushionSpringConfigType = { ...s, springs_enabled: true };

      // The cushion is being turned on for what looks like the first time
      // (no hardware picked yet AND no manual spring count). Treat all four
      // schema-default fields as "still empty" and overwrite them.
      const looksUntouched =
        !s.spring_hardware_id &&
        !s.clip_hardware_id &&
        !s.connecting_wire_hardware_id &&
        !(s.spring_mode === 'manual' && (s.spring_count ?? 0) > 0);

      if (!s.spring_hardware_id) {
        next.spring_hardware_id = findHwIdByName(SPRING_DEFAULT_HARDWARE_NAMES.spring);
      }
      if (!s.clip_hardware_id) {
        next.clip_hardware_id = findHwIdByName(SPRING_DEFAULT_HARDWARE_NAMES.clip);
      }
      if (!s.connecting_wire_hardware_id) {
        next.connecting_wire_hardware_id = findHwIdByName(SPRING_DEFAULT_HARDWARE_NAMES.connecting_wire);
      }
      if (looksUntouched) {
        next.spring_mode = 'auto';
        next.spring_spacing = SPRING_DEFAULT_SPACING;
        next.connecting_wire_count = 1;
      }
      return next;
    });
  }, [hardwareList]);

  // ── Template loader ──
  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    const tmpl = templates.find((t) => t.id === templateId);
    if (!tmpl) return;
    try {
      const tc = JSON.parse(tmpl.back_cushion_config || '{}');
      setCode(tc.code ?? tmpl.code);
      setName(tc.name ?? tmpl.name);
      if (tc.profile_depth != null) setProfileDepth(tc.profile_depth);
      if (tc.profile_height != null) setProfileHeight(tc.profile_height);
      if (tc.base_depth != null) setBaseDepth(tc.base_depth);
      if (tc.base_width != null) setBaseWidth(tc.base_width);
      if (tc.base_height != null) setBaseHeight(tc.base_height);
      if (tc.design) setDesign(tc.design);
      if (tc.framing) setFraming(tc.framing);
      if (tc.springs) {
        setSprings({
          ...DEFAULT_SPRINGS,
          ...tc.springs,
          springs_enabled:
            typeof tc.springs.springs_enabled === 'boolean'
              ? tc.springs.springs_enabled
              : deriveSpringsEnabledFromLegacy(tc.springs),
        });
      }
      if (tc.foam_layers) setFoamLayers(tc.foam_layers);
      if (tc.foam_waste_factor != null) setFoamWasteFactor(tc.foam_waste_factor);
      if (tc.foam_curved_waste_factor != null) setFoamCurvedWasteFactor(tc.foam_curved_waste_factor);
      if (tc.dacron_material_id !== undefined) setDacronMaterialId(tc.dacron_material_id);
      if (tc.dacron_waste_factor != null) setDacronWasteFactor(tc.dacron_waste_factor);
      if (tc.dacron_curved_waste_factor != null) setDacronCurvedWasteFactor(tc.dacron_curved_waste_factor);
    } catch { /* ignore */ }
  }, [templates]);

  // ── Build Config ──
  const buildConfig = useCallback((): BackCushionConfig => {
    return {
      product_type: 'banquette_back_cushion',
      code,
      name: name.trim(),
      lf_quantity: lfQuantity,
      manual_sections: manualSections,
      section_count: sectionCount,
      profile_height: profileHeight,
      profile_depth: profileDepth,
      base_depth: baseDepth,
      base_width: baseWidth,
      base_height: baseHeight,
      design,
      substrate_mode: 'full_frame' as const,
      backer_panel: null,
      framing,
      foam_layers: foamLayers,
      foam_waste_factor: foamWasteFactor,
      foam_curved_waste_factor: foamCurvedWasteFactor,
      dacron_material_id: dacronMaterialId,
      dacron_waste_factor: dacronWasteFactor,
      dacron_curved_waste_factor: dacronCurvedWasteFactor,
      springs,
      fabric_design_pct: fabricDesignPct,
      fabric_pattern_allowance_pct: fabricPatternAllowancePct,
      fabric_faces: fabricFaces,
      oversize_enabled: oversizeEnabled,
      viewport_id: viewportId,
      // Persist the page PPI so backend labor calc can compute fabric yards
      // for complexity scoring after save (live preview already uses pageScalePpi).
      image_scale_ppi: pageScalePpi ?? 0,
      slots,
    };
  }, [
    code, name, lfQuantity, manualSections, sectionCount, profileHeight, profileDepth, baseDepth, baseHeight, baseWidth,
    design, framing, foamLayers,
    foamWasteFactor, foamCurvedWasteFactor, dacronMaterialId, dacronWasteFactor, dacronCurvedWasteFactor,
    springs, fabricDesignPct, fabricPatternAllowancePct, fabricFaces, viewportId, slots, pageScalePpi, oversizeEnabled,
  ]);

  // ── Save ──
  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (widthInches <= 0) { toast.error('Width must be greater than 0'); return; }

    const backCushionConfig = buildConfig();
    const configJson = JSON.stringify(backCushionConfig);

    try {
      if (isEditMode) {
        await updateLineItem.mutateAsync({
          id: existingLineItemId!,
          back_cushion_config: configJson,
          cabinet_code: code,
          description: name.trim(),
          width: widthInches,
          height: profileHeight,
          depth: profileDepth,
        } as any);
        toast.success('Back cushion updated');
      } else {
        const createPayload: any = {
          area_id: areaId,
          cabinet_code: code,
          description: name.trim(),
          quantity: 1,
          width: widthInches, height: profileHeight, depth: profileDepth,
          num_doors: 0, num_drawers: 0, num_shelves: 0,
          back_cushion_config: configJson,
          template_id: null, finish_id: null,
          construction_method_id: null,
          construction_overrides: '{}',
          position_x: 0, position_y: 0, position_z: 0,
          is_in_3d_view: false,
          material_overrides: '{}',
          notes: null, sort_order: 0,
        };
        await createLineItem.mutateAsync(templateMode ? toTemplatePayload(createPayload) : createPayload);
        toast.success(templateMode ? 'Back cushion template created' : 'Back cushion added');
      }
      handleReset();
      onClose();
    } catch {
      toast.error(isEditMode ? 'Failed to update back cushion' : 'Failed to create back cushion');
    }
  };

  const handleReset = () => {
    setCode('BC');
    setName('');
    setWidthInches(120);
    setWidthInputStr('120');
    setProfileDepth(6);
    setProfileHeight(18);
    setBaseDepth(null);
    setBaseWidth(null);
    setBaseHeight(null);
    setManualSections(false);
    setManualSectionCount(1);
    setDesign(DEFAULT_DESIGN);
    setFraming(DEFAULT_FRAMING);
    setSprings(DEFAULT_SPRINGS);
    setFoamLayers([]);
    setFoamWasteFactor(0.10);
    setDacronMaterialId(null);
    setDacronWasteFactor(0.10);
    setViewportId(null);
    setSelectedTemplateId('');
    setOversizeEnabled(true);
    useBackCushionSlotStore.getState().clearAll();
  };

  // What the factor adds to the preview's Total ex-Fabric, for the oversize chip.
  const oversizeExtraCost =
    (costPreview.total_cost_excluding_fabric
      + (laborEstimate.data?.total_labor_cost ?? 0)
      + cnc.total_cost_per_unit)
    * (oversize.factor - 1);

  const isPending = createLineItem.isPending || updateLineItem.isPending;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-[var(--bg-surface)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent>
        <ModalHeader className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Armchair size={16} style={{ color: '#e67e22' }} />
          {isEditMode ? 'Edit Back Cushion' : 'New Back Cushion'}
        </ModalHeader>
        <ModalBody className="py-0 px-0 gap-0">
          {/* ── Sticky Cost Bar ── */}
          <div className="sticky top-0 z-10 px-6 py-2" style={{ backgroundColor: 'var(--bg-surface)' }}>
            <BackCushionCostPreview
              cost={costPreview}
              lfQuantity={lfQuantity}
              sectionCount={sectionCount}
              laborCost={laborEstimate.data?.total_labor_cost}
              laborDetails={laborEstimate.data?.labor_details}
              cncCost={cnc.total_cost_per_unit}
              oversizeFactor={isLibraryContext ? oversize.factor : 1}
            />
            {/* Library only: a project line item is never charged oversize. */}
            {isLibraryContext && (
              <div className="mt-1.5">
                <OversizeControl
                  enabled={oversizeEnabled}
                  onChange={setOversizeEnabled}
                  result={oversize}
                  extraCost={oversizeExtraCost}
                />
              </div>
            )}
          </div>

          {/* ── Single Column Config ── */}
          <div className="px-6 py-4 space-y-4">

            {/* 1. Template + Header row */}
            <div className="grid grid-cols-12 gap-3">
              {!isEditMode && templates.length > 0 && (
                <div className="col-span-4">
                  <Select
                    label="Template"
                    selectedKeys={selectedTemplateId ? [selectedTemplateId] : []}
                    onSelectionChange={(keys) => {
                      const id = Array.from(keys)[0] as string;
                      if (id) handleTemplateSelect(id);
                    }}
                    size="sm"
                    placeholder="Load template..."
                    classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
                  >
                    {templates.map((t) => (
                      <SelectItem key={t.id}>{t.code} — {t.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}
              <div className={!isEditMode && templates.length > 0 ? 'col-span-2' : 'col-span-3'}>
                <Input label="Code" value={code} onValueChange={setCode} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }} />
              </div>
              <div className={!isEditMode && templates.length > 0 ? 'col-span-6' : 'col-span-9'}>
                <Input label="Name *" value={name} onValueChange={setName} size="sm" isRequired
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }} />
              </div>
            </div>

            {/* 3. Dimensions */}
            <div>
              <h3 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Dimensions</h3>
              <div className="grid grid-cols-8 gap-3">
                <div>
                  <Input label="LF Qty (Width)" value={widthInputStr}
                    onValueChange={setWidthInputStr}
                    onBlur={() => {
                      const result = evalMathInput(widthInputStr);
                      if (result !== null && result >= 0) {
                        const rounded = Math.round(result);
                        setWidthInches(rounded);
                        setWidthInputStr(rounded.toString());
                      } else {
                        setWidthInputStr(widthInches.toString());
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    size="sm"
                    classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                    endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
                    description={widthInches > 0 ? inchesToFeetDisplay(widthInches) : undefined}
                  />
                </div>
                <Input label="Profile Height" type="number" value={profileHeight.toString()}
                  onValueChange={(v) => setProfileHeight(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Profile Depth" type="number" value={profileDepth.toString()}
                  onValueChange={(v) => setProfileDepth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Height" type="number"
                  value={(baseHeight ?? profileHeight).toString()}
                  onValueChange={(v) => setBaseHeight(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic panel reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Depth" type="number"
                  value={(baseDepth ?? profileDepth).toString()}
                  onValueChange={(v) => setBaseDepth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic panel reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                <Input label="Base Width" type="number"
                  value={(baseWidth ?? widthInches).toString()}
                  onValueChange={(v) => setBaseWidth(Number(v) || 0)} size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="Dynamic foam reference"
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>} />
                {/* Foam Thickness removed — thickness is now per foam layer */}
                <div className="col-span-2 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-1">
                    <Switch size="sm" isSelected={manualSections} onValueChange={setManualSections}>
                      <span className="text-xs">Manual sections</span>
                    </Switch>
                  </div>
                  {manualSections ? (
                    <Input type="number" value={manualSectionCount.toString()}
                      onValueChange={(v) => setManualSectionCount(Math.max(1, Number(v) || 1))}
                      size="sm" label="Sections"
                      classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }} />
                  ) : (
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {sectionCount} section{sectionCount !== 1 ? 's' : ''} x {sectionLength.toFixed(1)}" each
                      {sectionLength > 96 && <span style={{ color: '#e67e22' }}> (exceeds 96")</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Divider className="bg-[var(--border)]" />

            {/* 4. Design Type Selector */}
            <BackCushionDesignSelector
              design={design}
              onChange={setDesign}
              sectionWidthInches={sectionLength}
              profileHeight={profileHeight}
              hardwareOptions={hardwareOptions}
            />

            <Divider className="bg-[var(--border)]" />

            {/* 5. Framing Configuration */}
            <Accordion variant="bordered" defaultExpandedKeys={['framing']}>
              <AccordionItem key="framing" title="Framing Configuration"
                classNames={{ base: 'border-[var(--border)]', title: 'text-xs', trigger: 'py-2' }}>
                <BackCushionFramingConfig
                  framing={framing}
                  materials={materials as MaterialLibrary[]}
                  onChange={setFraming}
                  widthInches={widthInches}
                  sectionCount={sectionCount}
                  dims={{
                    width: widthInches,
                    depth: profileDepth,
                    height: profileHeight,
                    run_axis: 'width' as const,
                    base_width: baseWidth,
                    base_depth: baseDepth,
                    base_height: baseHeight,
                  }}
                />
              </AccordionItem>
            </Accordion>

            <Divider className="bg-[var(--border)]" />

            {/* 6. Spring Config */}
            <Accordion variant="bordered">
              <AccordionItem
                key="springs"
                title={
                  <div className="flex items-center justify-between w-full pr-2">
                    <span className="text-xs">Spring Configuration</span>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Switch
                        size="sm"
                        isSelected={!!springs.springs_enabled}
                        onValueChange={handleSpringsToggle}
                        aria-label="Enable spring configuration"
                      />
                    </div>
                  </div>
                }
                classNames={{ base: 'border-[var(--border)]', trigger: 'py-2' }}
              >
                <CushionSpringConfig
                  springs={springs}
                  onChange={setSprings}
                  hardwareOptions={hardwareOptions}
                  isDisabled={!springs.springs_enabled}
                />
              </AccordionItem>
            </Accordion>

            <Divider className="bg-[var(--border)]" />

            {/* 7. Foam Layers (like rails but with thickness) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold" style={{ color: '#e67e22' }}>Foam Layers</h3>
                <div className="flex items-center gap-2">
                  <Input
                    label="Waste"
                    type="number"
                    value={(foamWasteFactor * 100).toString()}
                    onValueChange={(v) => setFoamWasteFactor((Number(v) || 0) / 100)}
                    size="sm"
                    className="w-20"
                    classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                    endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
                  />
                  <Input
                    label="Curved Waste"
                    type="number"
                    value={(foamCurvedWasteFactor * 100).toString()}
                    onValueChange={(v) => setFoamCurvedWasteFactor((Number(v) || 0) / 100)}
                    size="sm"
                    className="w-24"
                    classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                    endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
                  />
                  <Button size="sm" variant="light" onPress={() => {
                    setFoamLayers([...foamLayers, { name: `Foam ${foamLayers.length + 1}`, width: 18, width_link: null, length: null, length_link: null, material_id: null }]);
                  }}>
                    + Add Layer
                  </Button>
                </div>
              </div>
              {(framing.curved_section_count ?? 0) > 0 && foamCurvedWasteFactor > 0 && (
                <div className="text-[10px] mb-2" style={{ color: 'var(--text-muted)' }}>
                  Effective foam waste:{' '}
                  {((foamWasteFactor
                     + foamCurvedWasteFactor
                       * Math.min(1, (framing.curved_section_count ?? 0) / Math.max(1, sectionCount))
                    ) * 100).toFixed(1)}%
                </div>
              )}
              <div className="space-y-2">
                {foamLayers.map((layer, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={layer.name}
                      onValueChange={(v) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], name: v };
                        setFoamLayers(updated);
                      }}
                      size="sm"
                      className="w-36"
                      classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                      placeholder="Name"
                    />
                    <Input
                      type="number"
                      value={layer.width.toString()}
                      onValueChange={(v) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], width: Number(v) || 0 };
                        setFoamLayers(updated);
                      }}
                      size="sm"
                      className="w-16"
                      classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                      endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>w</span>}
                    />
                    <PanelDimControls
                      label="Width"
                      link={layer.width_link ?? null}
                      onChange={(link) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], width_link: link };
                        setFoamLayers(updated);
                      }}
                    />
                    <Input
                      type="number"
                      placeholder="auto"
                      value={layer.length == null ? '' : layer.length.toString()}
                      onValueChange={(v) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], length: v === '' ? null : (Number(v) || 0) };
                        setFoamLayers(updated);
                      }}
                      size="sm"
                      className="w-16"
                      classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                      endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>L</span>}
                    />
                    <PanelDimControls
                      label="Length"
                      link={layer.length_link ?? null}
                      onChange={(link) => {
                        const updated = [...foamLayers];
                        // Leaving Auto has to concretize a base, or the row would
                        // show an empty input with no number to edit.
                        const base = updated[i].length ?? Math.round(widthInches);
                        updated[i] = { ...updated[i], length: base, length_link: link };
                        setFoamLayers(updated);
                      }}
                    />
                    <Autocomplete
                      selectedKey={layer.material_id ?? undefined}
                      onSelectionChange={(key) => {
                        const updated = [...foamLayers];
                        updated[i] = { ...updated[i], material_id: (key as string) || null };
                        setFoamLayers(updated);
                      }}
                      size="sm"
                      placeholder="Material"
                      className="flex-1"
                      inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
                    >
                      {materials.map((m: any) => (
                        <AutocompleteItem key={m.id}>{m.name}</AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={() => setFoamLayers(foamLayers.filter((_, idx) => idx !== i))}
                    >
                      <span style={{ color: 'var(--text-muted)' }}>🗑</span>
                    </Button>
                    {resolvedFoam[i] && (
                      <span
                        className="text-[10px] flex-shrink-0 w-32 text-right"
                        style={{ color: resolvedFoam[i].collapsed ? '#e67e22' : 'var(--text-muted)' }}
                      >
                        {resolvedFoam[i].collapsed
                          ? 'collapsed'
                          : `${resolvedFoam[i].width} x ${Math.round(resolvedFoam[i].length)} in · ${((resolvedFoam[i].width * resolvedFoam[i].length) / 144).toFixed(1)} sqft`}
                      </span>
                    )}
                  </div>
                ))}
                {foamLayers.length === 0 && (
                  <div className="text-xs py-2 text-center" style={{ color: 'var(--text-muted)' }}>
                    No foam layers. Click "+ Add Layer" to add.
                  </div>
                )}
              </div>
            </div>

            {/* 8. Dacron material picker */}
            <div>
              <h3 className="text-xs font-semibold mb-2" style={{ color: '#e67e22' }}>Dacron</h3>
              <div className="grid grid-cols-3 gap-3">
                <Autocomplete
                  label="Dacron Material"
                  selectedKey={dacronMaterialId ?? undefined}
                  onSelectionChange={(key) => {
                    setDacronMaterialId((key as string) || null);
                  }}
                  size="sm"
                  placeholder="Select dacron material..."
                  inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
                >
                  {materials.map((m: any) => (
                    <AutocompleteItem key={m.id}>{m.name}</AutocompleteItem>
                  ))}
                </Autocomplete>
                <Input
                  label="Cost / sqft"
                  value={(() => {
                    if (!dacronMaterialId) return '—';
                    const m = materials.find((mat: any) => mat.id === dacronMaterialId) as any;
                    if (!m) return '—';
                    // One square foot of this material, costed the way the
                    // cost helpers cost it. Going through priceMaterialByUnit
                    // keeps this readout honest for every unit spelling —
                    // comparing `m.unit` against 'sq ft' / 'lin ft' literals
                    // here showed a linear_foot material's per-LF price as if
                    // it were already per-sqft.
                    const perSqft = priceMaterialByUnit(1, m, 0, { pieceQty: 1 });
                    if (perSqft.warnings.length > 0) return '—';
                    return `$${perSqft.cost.toFixed(perSqft.cost < 1 ? 4 : 2)}`;
                  })()}
                  isReadOnly
                  size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  description="From material"
                />
                <Input
                  label="Dacron Waste Factor"
                  type="number"
                  value={(dacronWasteFactor * 100).toString()}
                  onValueChange={(v) => setDacronWasteFactor((Number(v) || 0) / 100)}
                  size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
                />
                <Input
                  label="Dacron Curved Waste"
                  type="number"
                  value={(dacronCurvedWasteFactor * 100).toString()}
                  onValueChange={(v) => setDacronCurvedWasteFactor((Number(v) || 0) / 100)}
                  size="sm"
                  classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
                  endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
                />
              </div>
              {(framing.curved_section_count ?? 0) > 0 && dacronCurvedWasteFactor > 0 && (
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Effective dacron waste:{' '}
                  {((dacronWasteFactor
                     + dacronCurvedWasteFactor
                       * Math.min(1, (framing.curved_section_count ?? 0) / Math.max(1, sectionCount))
                    ) * 100).toFixed(1)}%
                </div>
              )}
            </div>

            <Divider className="bg-[var(--border)]" />

            {/* 9. Viewport + Slot Manager */}
            <div className="grid grid-cols-2 gap-4">
              <BanquetteViewportSelector
                projectId={projectId}
                viewportId={viewportId}
                onViewportChange={(id) => setViewportId(id)}
              />
              <div className="flex flex-col justify-end">
                <Button
                  fullWidth
                  variant="bordered"
                  onPress={slotManagerModal.onOpen}
                  isDisabled={!viewportId}
                  style={{ borderColor: '#e67e22', color: '#e67e22' }}
                >
                  Open Slot Manager ({slots.length} slot{slots.length !== 1 ? 's' : ''})
                </Button>
              </div>
            </div>

            {/* Finish assignments for slots with materials requiring finish */}
            {slots.length > 0 && (
              <>
                <Divider className="bg-[var(--border)]" />
                <BackCushionFinishSummary
                  slots={slots}
                  materials={materials as MaterialLibrary[]}
                  onUpdateSlot={updateSlot}
                />
              </>
            )}

            <Divider className="bg-[var(--border)]" />

            {/* Fabric Allowances */}
            {(() => {
              const isFabricMat = (m: any) => !!(m?.fabric_subtype) || ((m?.material_type ?? '') as string).toLowerCase() === 'fabric';
              const hasFabricSlot = slots.some((s) => {
                if (!s.material_id) return false;
                return isFabricMat(materialsMap.get(s.material_id));
              });
              if (!hasFabricSlot) return null;
              // Find the first fabric material's default design pct for placeholder
              const firstFabricMat = slots
                .filter((s) => s.material_id)
                .map((s) => materialsMap.get(s.material_id!))
                .find((m) => isFabricMat(m));
              const matDefault = (firstFabricMat as any)?.fabric_design_pct ?? 0;
              return (
                <div className="flex gap-3">
                  <Input
                    label="Design Allowance (%)"
                    type="number"
                    size="sm"
                    className="w-44"
                    value={fabricDesignPct != null ? String(fabricDesignPct * 100) : ''}
                    placeholder={
                      resolvedDesignPct != null
                        ? `Inherited: ${(resolvedDesignPct * 100).toFixed(0)}%`
                        : `Mat default: ${(matDefault * 100).toFixed(0)}%`
                    }
                    onValueChange={(v) => {
                      if (v === '') { setFabricDesignPct(null); return; }
                      setFabricDesignPct((Number(v) || 0) / 100);
                    }}
                    description="Overrides design type setting"
                  />
                  <Input
                    label="Pattern Allowance (%)"
                    type="number"
                    size="sm"
                    className="w-44"
                    value={String(fabricPatternAllowancePct * 100)}
                    onValueChange={(v) => setFabricPatternAllowancePct((Number(v) || 0) / 100)}
                    description="Pattern repeat allowance"
                  />
                </div>
              );
            })()}

            <FabricCoverageSection
              productType="banquette_back_cushion"
              dims={{ width_in: sectionLength, height_in: profileHeight, depth_in: profileDepth }}
              value={fabricFaces}
              onChange={setFabricFaces}
            />

            {/* Finish Schedule (read-only summary grouped by material type) */}
            <Accordion variant="bordered">
              <AccordionItem
                key="finish-schedule"
                title="Finish Schedule"
                classNames={{ base: 'border-[var(--border)]', title: 'text-xs', trigger: 'py-2' }}
              >
                <FinishSchedule
                  slotDetails={costPreview?.slot_details ?? []}
                  materials={materials as MaterialLibrary[]}
                  fabricPackings={costPreview?.fabric_packings ?? []}
                />
              </AccordionItem>
              <AccordionItem
                key="nesting-preview"
                title="Nesting Preview"
                classNames={{ base: 'border-[var(--border)]', title: 'text-xs', trigger: 'py-2' }}
              >
                <ComponentNestingPanel
                  parts={costPreview?.nesting_parts ?? []}
                  materials={materials as MaterialLibrary[]}
                  cnc={cnc}
                />
              </AccordionItem>
            </Accordion>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button
            onPress={handleSave}
            isLoading={isPending}
            style={{ backgroundColor: '#e67e22', color: 'white' }}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Slot Manager fullscreen modal */}
    {viewportId && (
      <BackCushionSlotManager
        isOpen={slotManagerModal.isOpen}
        onClose={slotManagerModal.onClose}
        viewportId={viewportId}
        projectId={projectId}
        productType="banquette_back_cushion"
        profileDepth={profileDepth}
        profileHeight={profileHeight}
      />
    )}
    </>
  );
}
